/**
 * Persist MUI DataGridPro layout state (columns, sort, filter, pins, density, …)
 * to localStorage, and restore it on mount.
 *
 * MUI only applies `initialState` on first mount, so "Reset Layout" remounts the
 * grid via `layoutKey` rather than mutating live state.
 *
 * Pass `undefined` when persistence is disabled — the hook stays callable from
 * RATDataGrid unconditionally (React hooks rules).
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  GridApiPro,
  GridInitialState,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

export type GridPersistenceConfig = {
  /** localStorage key for this grid's saved state. */
  readonly storageKey: string;
  /** Current column field names — used to strip stale fields from saved state. */
  readonly validFields: readonly string[];
  /** Factory defaults used when nothing is stored, and after Reset Layout. */
  readonly defaultInitialState: GridInitialState;
  /** Fields re-inserted at the front of orderedFields if missing after sanitize. */
  readonly ensureFirstFields?: readonly string[];
  /** Fallback left-pin list when saved pins are empty / invalid. */
  readonly defaultPinnedLeft?: readonly string[];
  /** Debounce for stateChange → localStorage writes. Defaults to 300ms. */
  readonly debounceMs?: number;
};

export type UseGridStatePersistenceResult = {
  readonly apiRef: ReturnType<typeof useGridApiRef<GridApiPro>>;
  /** Passed to DataGridPro as initialState (undefined when persistence is off). */
  readonly initialState: GridInitialState | undefined;
  /**
   * Change this as DataGridPro `key` to force a remount (needed for Reset Layout,
   * because initialState is mount-only).
   */
  readonly layoutKey: number;
  /** Clear storage and remount with defaultInitialState. Undefined when persistence is off. */
  readonly resetGridState: (() => void) | undefined;
};

const DEFAULT_DEBOUNCE_MS = 300;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Loose shape check — full validation is done by sanitizeGridState. */
function isGridInitialState(value: unknown): value is GridInitialState {
  return isRecord(value);
}

function filterColumnFields(
  fields: readonly string[] | undefined,
  validFields: ReadonlySet<string>
): string[] {
  if (!fields?.length) {
    return [];
  }

  return fields.filter((field) => validFields.has(field));
}

/**
 * Drop unknown column fields from a previously saved state so column renames /
 * removals don't leave the grid in a broken configuration.
 */
function sanitizeGridState(
  state: GridInitialState,
  config: Pick<
    GridPersistenceConfig,
    | "validFields"
    | "ensureFirstFields"
    | "defaultPinnedLeft"
    | "defaultInitialState"
  >
): GridInitialState {
  const validFields = new Set(config.validFields);
  const sanitized: GridInitialState = { ...state };

  if (state.columns) {
    const orderedFields = filterColumnFields(
      state.columns.orderedFields,
      validFields
    );

    // Reverse + unshift preserves ensureFirstFields order when multiple are missing.
    for (const field of [...(config.ensureFirstFields ?? [])].reverse()) {
      if (validFields.has(field) && !orderedFields.includes(field)) {
        orderedFields.unshift(field);
      }
    }

    // Newly added columns should still appear for previously saved layouts.
    for (const field of config.validFields) {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    }

    const columnVisibilityModel = Object.fromEntries(
      Object.entries(state.columns.columnVisibilityModel ?? {}).filter(
        ([field]) => validFields.has(field)
      )
    );
    const defaultVisibility =
      config.defaultInitialState.columns?.columnVisibilityModel ?? {};
    for (const [field, visible] of Object.entries(defaultVisibility)) {
      if (!(field in columnVisibilityModel)) {
        columnVisibilityModel[field] = visible;
      }
    }
    const dimensions = Object.fromEntries(
      Object.entries(state.columns.dimensions ?? {}).filter(([field]) =>
        validFields.has(field)
      )
    );

    sanitized.columns = {
      ...state.columns,
      ...(Object.keys(columnVisibilityModel).length > 0
        ? { columnVisibilityModel }
        : {}),
      ...(orderedFields.length > 0 ? { orderedFields } : {}),
      ...(Object.keys(dimensions).length > 0 ? { dimensions } : {}),
    };
  }

  const pinnedLeft = (state.pinnedColumns?.left ?? []).filter((field) =>
    validFields.has(field)
  );
  const pinnedRight = (state.pinnedColumns?.right ?? []).filter((field) =>
    validFields.has(field)
  );
  const defaultPinnedLeft = (config.defaultPinnedLeft ?? []).filter((field) =>
    validFields.has(field)
  );

  if (pinnedLeft.length > 0 || pinnedRight.length > 0 || !state.pinnedColumns) {
    sanitized.pinnedColumns = {
      ...(pinnedLeft.length > 0
        ? { left: pinnedLeft }
        : defaultPinnedLeft.length > 0
        ? { left: [...defaultPinnedLeft] }
        : {}),
      ...(pinnedRight.length > 0 ? { right: pinnedRight } : {}),
    };
  }

  if (state.sorting?.sortModel?.length) {
    sanitized.sorting = {
      ...state.sorting,
      sortModel: state.sorting.sortModel.filter(({ field }) =>
        validFields.has(field)
      ),
    };
  }

  if (state.filter?.filterModel) {
    const filterModel = state.filter.filterModel;
    const items = (filterModel.items ?? []).filter(({ field }) =>
      validFields.has(field)
    );
    const quickFilterValues = filterModel.quickFilterValues ?? [];

    if (items.length > 0 || quickFilterValues.length > 0) {
      sanitized.filter = {
        ...state.filter,
        filterModel: {
          ...filterModel,
          items,
          ...(quickFilterValues.length > 0 ? { quickFilterValues } : {}),
        },
      };
    }
  }

  return sanitized;
}

function loadGridState(
  config: Pick<
    GridPersistenceConfig,
    | "storageKey"
    | "validFields"
    | "ensureFirstFields"
    | "defaultPinnedLeft"
    | "defaultInitialState"
  >
): GridInitialState | null {
  try {
    const raw = localStorage.getItem(config.storageKey);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isGridInitialState(parsed)) return null;

    return sanitizeGridState(parsed, config);
  } catch {
    // Corrupt JSON, private mode, etc. — fall back to defaults via caller.
    return null;
  }
}

function saveGridState(storageKey: string, state: GridInitialState): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // Ignore quota or serialization errors.
  }
}

function clearGridState(storageKey: string): void {
  localStorage.removeItem(storageKey);
}

function getGridInitialState(config: GridPersistenceConfig): GridInitialState {
  return loadGridState(config) ?? config.defaultInitialState;
}

export function useRATDataGridStatePersistence(
  config: GridPersistenceConfig | undefined
): UseGridStatePersistenceResult {
  const apiRef = useGridApiRef<GridApiPro>();
  const saveTimeoutRef = useRef<number>();
  /**
   * Blocks localStorage writes during Reset Layout remount. Remount fires a burst
   * of stateChange events that would otherwise re-save the old layout.
   */
  const skipPersistRef = useRef(false);
  /** Always-current config for callbacks without recreating them each render. */
  const configRef = useRef(config);
  configRef.current = config;

  /**
   * Snapshot of restored (or default) state from first mount only. Re-reading
   * localStorage on every render would fight in-session edits.
   */
  const mountInitialStateRef = useRef<GridInitialState | undefined>(
    config ? getGridInitialState(config) : undefined
  );
  const [layoutKey, setLayoutKey] = useState(0);

  // First mount: restored snapshot. After reset remount: factory defaults.
  const initialState = !config
    ? undefined
    : layoutKey === 0
    ? mountInitialStateRef.current
    : config.defaultInitialState;

  const persistState = useCallback(() => {
    const currentConfig = configRef.current;
    if (!currentConfig || !apiRef.current || skipPersistRef.current) {
      return;
    }

    saveGridState(currentConfig.storageKey, apiRef.current.exportState());
  }, [apiRef]);

  /** Coalesce rapid stateChange events (typing in quick filter, dragging columns, …). */
  const schedulePersistState = useCallback(() => {
    if (!configRef.current || skipPersistRef.current) {
      return;
    }

    const debounceMs = configRef.current.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    window.clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = window.setTimeout(persistState, debounceMs);
  }, [persistState]);

  /**
   * Clear saved layout and remount the grid with defaultInitialState.
   * Consumers must pass `layoutKey` as DataGridPro's `key` for remounting to work.
   */
  const resetGridState = useCallback(() => {
    const currentConfig = configRef.current;
    if (!currentConfig) {
      return;
    }

    skipPersistRef.current = true;
    clearGridState(currentConfig.storageKey);
    window.clearTimeout(saveTimeoutRef.current);
    setLayoutKey((currentKey) => currentKey + 1);
  }, []);

  // Re-enable persistence after the remount from Reset Layout has settled.
  useEffect(() => {
    if (!config || layoutKey === 0) {
      return;
    }

    skipPersistRef.current = false;
  }, [config, layoutKey]);

  // Re-subscribe after remount (layoutKey) so the new api instance is watched.
  useEffect(() => {
    if (!config || !apiRef.current) {
      return undefined;
    }

    return apiRef.current.subscribeEvent("stateChange", schedulePersistState);
  }, [apiRef, config, layoutKey, schedulePersistState]);

  // Flush pending edits on tab close / leave page, and on hook unmount.
  useLayoutEffect(() => {
    if (!config) {
      return undefined;
    }

    const handleBeforeUnload = () => {
      persistState();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.clearTimeout(saveTimeoutRef.current);
      persistState();
    };
  }, [config, persistState]);

  return {
    apiRef,
    initialState,
    layoutKey: config ? layoutKey : 0,
    resetGridState: config ? resetGridState : undefined,
  };
}
